import { useEffect } from 'react';

import { AuthService, SignOutReason, isTokenExpired } from '@suntex.fim/auth';

/**
 * Checks whether the current JWT is expired whenever the user returns to
 * the tab (visibilitychange → visible) or the window regains focus.
 * If expired, calls authService.signOut({ reason: SignOutReason.Expired }) to
 * trigger the session expired modal.
 */
export function useSessionExpiration(authService: AuthService): void {
  useEffect(() => {
    function checkExpiration() {
      const { jwt, isAuthenticated, signingOut } = authService.getState();
      if (!isAuthenticated || signingOut || !jwt) return;
      if (isTokenExpired(jwt)) {
        void authService.signOut({ reason: SignOutReason.Expired });
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        checkExpiration();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkExpiration);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkExpiration);
    };
  }, [authService]);
}
